import { MODE_REGISTRY } from '@game/game/ModeRegistry';
import { PauseMenu } from '@game/ui/PauseMenu';
import { DEFAULT_GAME_SETTINGS, type CameraViewMode, type GameModeId, type GameSettings, type GraphicsQuality } from '@game/types';
import type { Game } from '@game/game/Game';
import type { TitleScene } from '@game/ui/TitleScene';
import { ColyseusTransport } from '@game/network/ColyseusTransport';
import { PartyTransport } from '@game/network/PartyTransport';
import type { OfficialLobbyListing, OfficialServerListing } from '@shared/data/officialServers.js';
import { officialMapById } from '@shared/data/mapCatalog.js';

const DEFAULT_EDGE_HOST = 'voxel-game-edge.jilrot.workers.dev';
const PLAYER_NAME_STORAGE_KEY = 'voxel.playerName';
const PLAYER_SKIN_STORAGE_KEY = 'voxel.playerSkinId';
const GAME_SETTINGS_STORAGE_KEY = 'voxel.settings';
const DEFAULT_SKIN_IDS = [
  'blocky_a', 'blocky_b', 'blocky_c', 'blocky_d', 'blocky_e', 'blocky_f',
  'blocky_g', 'blocky_h', 'blocky_i', 'blocky_j', 'blocky_k', 'blocky_l',
  'blocky_m', 'blocky_n', 'blocky_o', 'blocky_p', 'blocky_q', 'blocky_r',
] as const;

const QUALITY_PRESETS: Record<GraphicsQuality, Pick<GameSettings, 'renderDistance' | 'resolutionScale' | 'maxFps'>> = {
  low: { renderDistance: 1, resolutionScale: 0.7, maxFps: 30 },
  medium: { renderDistance: 2, resolutionScale: 1, maxFps: 30 },
  high: { renderDistance: 3, resolutionScale: 1.25, maxFps: 45 },
  ultra: { renderDistance: 5, resolutionScale: 1.5, maxFps: 60 },
};

interface PlayRequest {
  readonly modeId: GameModeId;
  readonly templateId?: string;
  readonly mapFile?: File;
  readonly edgeHost?: string;
  readonly roomId?: string;
  readonly locationHint?: string;
  readonly playerName?: string;
  readonly playerSkinId?: string;
  readonly settings: GameSettings;
}

function isGameModeId(value: string): value is GameModeId {
  return value in MODE_REGISTRY;
}

function isGraphicsQuality(value: string): value is GraphicsQuality {
  return value === 'low' || value === 'medium' || value === 'high' || value === 'ultra';
}

function isCameraViewMode(value: string): value is CameraViewMode {
  return value === 'fpv' || value === 'tpv';
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function numberSetting(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function booleanSetting(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function normalizeGameSettings(settings?: unknown): GameSettings {
  const raw = settings !== null && typeof settings === 'object'
    ? settings as Partial<Record<keyof GameSettings, unknown>>
    : {};
  const quality = typeof raw.quality === 'string' && isGraphicsQuality(raw.quality)
    ? raw.quality
    : DEFAULT_GAME_SETTINGS.quality;
  return {
    quality,
    renderDistance: Math.round(clampNumber(
      numberSetting(raw.renderDistance, DEFAULT_GAME_SETTINGS.renderDistance),
      1,
      5,
    )),
    resolutionScale: clampNumber(
      numberSetting(raw.resolutionScale, DEFAULT_GAME_SETTINGS.resolutionScale),
      0.6,
      1.75,
    ),
    maxFps: Math.round(clampNumber(
      numberSetting(raw.maxFps, DEFAULT_GAME_SETTINGS.maxFps),
      20,
      60,
    )),
    showFps: booleanSetting(raw.showFps, DEFAULT_GAME_SETTINGS.showFps),
    showDiagnostics: booleanSetting(raw.showDiagnostics, DEFAULT_GAME_SETTINGS.showDiagnostics),
    footView: typeof raw.footView === 'string' && isCameraViewMode(raw.footView)
      ? raw.footView
      : DEFAULT_GAME_SETTINGS.footView,
    vehicleView: typeof raw.vehicleView === 'string' && isCameraViewMode(raw.vehicleView)
      ? raw.vehicleView
      : DEFAULT_GAME_SETTINGS.vehicleView,
  };
}

function loadGameSettings(): GameSettings {
  const stored = window.localStorage.getItem(GAME_SETTINGS_STORAGE_KEY);
  if (stored === null) {
    return { ...DEFAULT_GAME_SETTINGS };
  }
  try {
    return normalizeGameSettings(JSON.parse(stored));
  } catch {
    return { ...DEFAULT_GAME_SETTINGS };
  }
}

function saveGameSettings(settings: GameSettings): void {
  window.localStorage.setItem(GAME_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
}

function defaultPlayerName(): string {
  const stored = window.localStorage.getItem(PLAYER_NAME_STORAGE_KEY);
  if (stored !== null && stored.trim() !== '') {
    return stored.slice(0, 32);
  }
  const generated = `Player_${Math.random().toString(36).slice(2, 8)}`;
  window.localStorage.setItem(PLAYER_NAME_STORAGE_KEY, generated);
  return generated;
}

function defaultPlayerSkinId(): string {
  const stored = window.localStorage.getItem(PLAYER_SKIN_STORAGE_KEY);
  if (stored !== null && DEFAULT_SKIN_IDS.includes(stored as (typeof DEFAULT_SKIN_IDS)[number])) {
    return stored;
  }
  const generated = DEFAULT_SKIN_IDS[Math.floor(Math.random() * DEFAULT_SKIN_IDS.length)] ?? DEFAULT_SKIN_IDS[0];
  window.localStorage.setItem(PLAYER_SKIN_STORAGE_KEY, generated);
  return generated;
}

function normalizeEdgeHost(rawHost: string): string {
  return rawHost
    .trim()
    .replace(/^https?:\/\//, '')
    .replace(/^wss?:\/\//, '')
    .replace(/\/+$/, '');
}

function edgeHttpOrigin(host: string): string {
  const normalized = normalizeEdgeHost(host);
  const protocol =
    normalized.startsWith('localhost') ||
    normalized.startsWith('127.') ||
    normalized.startsWith('[::1]')
      ? 'http'
      : 'https';
  return `${protocol}://${normalized}`;
}

// ── DOM refs ──

const canvasEl = document.getElementById('renderCanvas');
if (!(canvasEl instanceof HTMLCanvasElement)) {
  throw new Error('renderCanvas must be a <canvas> element');
}
/** Narrowed reference safe to pass as HTMLCanvasElement. */
const canvas: HTMLCanvasElement = canvasEl;

const loadingEl = document.getElementById('loading');
const loadingBarFill = document.getElementById('loading-bar-fill');
const loadingSub = document.getElementById('loading-sub');

function showLoading(text: string): void {
  if (loadingEl !== null) {
    loadingEl.style.display = '';
  }
  if (loadingSub !== null) {
    loadingSub.textContent = text;
  }
}

function setLoadingProgress(pct: number, label: string): void {
  if (loadingEl !== null) {
    loadingEl.style.display = '';
  }
  if (loadingBarFill !== null) {
    loadingBarFill.style.width = `${String(Math.max(0, Math.min(1, pct)) * 100)}%`;
  }
  if (loadingSub !== null) {
    loadingSub.textContent = label;
  }
}

function hideLoading(): void {
  if (loadingEl !== null) {
    loadingEl.style.display = 'none';
  }
}

// ── Start Menu ──

function setupStartMenu(onPlay: (request: PlayRequest) => void): void {
  const menu = document.getElementById('start-menu');
  const menuModes = document.getElementById('menu-modes');
  const settingsBtn = document.getElementById('settings-btn') as HTMLButtonElement | null;
  const settingsModal = document.getElementById('settings-modal');
  const settingsCloseBtn = document.getElementById('settings-close-btn') as HTMLButtonElement | null;
  const settingsQualityOptions = document.getElementById('settings-quality-options');
  const settingsFootViewOptions = document.getElementById('settings-foot-view-options');
  const settingsVehicleViewOptions = document.getElementById('settings-vehicle-view-options');
  const renderDistanceInput = document.getElementById('settings-render-distance') as HTMLInputElement | null;
  const renderDistanceValue = document.getElementById('settings-render-distance-value');
  const resolutionScaleInput = document.getElementById('settings-resolution-scale') as HTMLInputElement | null;
  const resolutionScaleValue = document.getElementById('settings-resolution-scale-value');
  const fpsCapInput = document.getElementById('settings-fps-cap') as HTMLInputElement | null;
  const fpsCapValue = document.getElementById('settings-fps-cap-value');
  const showFpsInput = document.getElementById('settings-show-fps') as HTMLInputElement | null;
  const showDiagnosticsInput = document.getElementById('settings-show-diagnostics') as HTMLInputElement | null;
  const playerNameInput = document.getElementById('player-name-input') as HTMLInputElement | null;
  const importMapBtn = document.getElementById('import-map-btn') as HTMLButtonElement | null;
  const importMapInput = document.getElementById('import-map-input') as HTMLInputElement | null;
  const multiplayerBtn = document.getElementById('multiplayer-btn') as HTMLButtonElement | null;
  const serverBrowserOverlay = document.getElementById('server-browser-overlay');
  const serverBrowserClose = document.getElementById('server-browser-close') as HTMLButtonElement | null;
  const serverList = document.getElementById('server-list');
  const lobbyList = document.getElementById('lobby-list');
  const serverBrowserStatus = document.getElementById('server-browser-status');
  const serverBrowserPlayBtn = document.getElementById('server-browser-play-btn') as HTMLButtonElement | null;

  if (
    menu === null || menuModes === null ||
    settingsBtn === null || settingsModal === null || settingsCloseBtn === null ||
    settingsQualityOptions === null || settingsFootViewOptions === null || settingsVehicleViewOptions === null ||
    renderDistanceInput === null || renderDistanceValue === null ||
    resolutionScaleInput === null || resolutionScaleValue === null ||
    fpsCapInput === null || fpsCapValue === null ||
    showFpsInput === null || showDiagnosticsInput === null ||
    playerNameInput === null || importMapBtn === null || importMapInput === null ||
    multiplayerBtn === null || serverBrowserOverlay === null || serverBrowserClose === null ||
    serverList === null || lobbyList === null || serverBrowserStatus === null || serverBrowserPlayBtn === null
  ) {
    throw new Error('Start menu DOM elements missing');
  }

  const controller = new AbortController();
  const params = new URLSearchParams(window.location.search);
  const edgeHost = normalizeEdgeHost(params.get('edge') ?? DEFAULT_EDGE_HOST);

  playerNameInput.value = defaultPlayerName();
  let gameSettings = loadGameSettings();

  // ── Ambient particles ──
  const particlesContainer = document.getElementById('menu-particles');
  if (particlesContainer !== null) {
    for (let i = 0; i < 30; i += 1) {
      const particle = document.createElement('div');
      particle.className = 'menu-particle';
      particle.style.left = `${String(Math.random() * 100)}%`;
      particle.style.animationDuration = `${String(8 + Math.random() * 14)}s`;
      particle.style.animationDelay = `${String(Math.random() * 10)}s`;
      particle.style.width = `${String(1 + Math.random() * 2)}px`;
      particle.style.height = particle.style.width;
      particlesContainer.appendChild(particle);
    }
  }

  // ── Settings ──
  const syncSettingsUi = (): void => {
    for (const button of settingsQualityOptions.querySelectorAll<HTMLButtonElement>('[data-quality]')) {
      const quality = button.dataset['quality'];
      button.classList.toggle('selected', quality === gameSettings.quality);
    }
    for (const button of settingsFootViewOptions.querySelectorAll<HTMLButtonElement>('[data-foot-view]')) {
      button.classList.toggle('selected', button.dataset['footView'] === gameSettings.footView);
    }
    for (const button of settingsVehicleViewOptions.querySelectorAll<HTMLButtonElement>('[data-vehicle-view]')) {
      button.classList.toggle('selected', button.dataset['vehicleView'] === gameSettings.vehicleView);
    }
    renderDistanceInput.value = String(gameSettings.renderDistance);
    renderDistanceValue.textContent = String(gameSettings.renderDistance);
    resolutionScaleInput.value = String(Math.round(gameSettings.resolutionScale * 100));
    resolutionScaleValue.textContent = `${String(Math.round(gameSettings.resolutionScale * 100))}%`;
    fpsCapInput.value = String(gameSettings.maxFps);
    fpsCapValue.textContent = String(gameSettings.maxFps);
    showFpsInput.checked = gameSettings.showFps;
    showDiagnosticsInput.checked = gameSettings.showDiagnostics;
  };

  const persistSettings = (settings: GameSettings): void => {
    gameSettings = normalizeGameSettings(settings);
    saveGameSettings(gameSettings);
    syncSettingsUi();
  };

  const openSettings = (): void => {
    syncSettingsUi();
    settingsModal.hidden = false;
    settingsModal.setAttribute('aria-hidden', 'false');
  };

  const closeSettings = (): void => {
    settingsModal.hidden = true;
    settingsModal.setAttribute('aria-hidden', 'true');
  };

  const leaveMenu = (request: PlayRequest): void => {
    controller.abort();
    settingsModal.hidden = true;
    serverBrowserOverlay.classList.remove('open');
    menu.classList.add('leaving');
    window.setTimeout(() => {
      menu.classList.add('hidden');
      menu.classList.remove('leaving');
      onPlay(request);
    }, 180);
  };

  // ── Mode cards: click to play immediately ──
  menuModes.addEventListener('click', (event) => {
    const card = (event.target as HTMLElement).closest<HTMLButtonElement>('.menu-mode-card');
    if (card === null) return;
    const mode = card.dataset['mode'];
    const map = card.dataset['map'];
    if (mode === undefined || !isGameModeId(mode)) return;
    const playerName = playerNameInput.value.trim().slice(0, 32) || defaultPlayerName();
    window.localStorage.setItem(PLAYER_NAME_STORAGE_KEY, playerName);
    leaveMenu({
      modeId: mode,
      ...(map === undefined ? {} : { templateId: map }),
      playerName,
      playerSkinId: defaultPlayerSkinId(),
      settings: gameSettings,
    });
  }, { signal: controller.signal });

  // ── Multiplayer browser ──
  let mpServers: readonly OfficialServerListing[] = [];
  let mpLobbies: readonly OfficialLobbyListing[] = [];
  let mpSelectedServer: OfficialServerListing | null = null;
  let mpSelectedLobby: OfficialLobbyListing | null = null;

  const renderServerList = (): void => {
    serverList.textContent = '';
    for (const server of mpServers) {
      const entry = document.createElement('button');
      entry.type = 'button';
      entry.className = 'server-entry';
      if (server.id === mpSelectedServer?.id) entry.classList.add('selected');
      entry.innerHTML = `<div class="server-entry-name">${server.label}</div><div class="server-entry-meta">${server.locationHint.toUpperCase()} · ${String(server.lobbyCount)} rooms</div>`;
      entry.addEventListener('click', () => {
        mpSelectedServer = server;
        mpSelectedLobby = null;
        renderServerList();
        void loadMpLobbies(server.id);
      }, { signal: controller.signal });
      serverList.appendChild(entry);
    }
  };

  const renderLobbyList = (): void => {
    lobbyList.textContent = '';
    if (mpLobbies.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'lobby-empty-state';
      empty.textContent = 'No rooms available';
      lobbyList.appendChild(empty);
      serverBrowserPlayBtn.disabled = true;
      return;
    }
    for (const lobby of mpLobbies) {
      const modeId = isGameModeId(lobby.mode) ? lobby.mode : 'freePlay';
      const rules = MODE_REGISTRY[modeId];
      const mapInfo = officialMapById(lobby.mapId);
      const entry = document.createElement('button');
      entry.type = 'button';
      entry.className = 'lobby-entry';
      if (lobby.id === mpSelectedLobby?.id) entry.classList.add('selected');
      entry.innerHTML = `<div class="lobby-entry-name">${lobby.name}</div><div class="lobby-entry-meta">${rules.name} · ${mapInfo?.name ?? lobby.mapId} · ${String(lobby.playerCount)}/${String(lobby.maxPlayers)}</div><div class="lobby-entry-desc">${lobby.description}</div>`;
      entry.addEventListener('click', () => {
        mpSelectedLobby = lobby;
        renderLobbyList();
        serverBrowserPlayBtn.disabled = false;
      }, { signal: controller.signal });
      lobbyList.appendChild(entry);
    }
    serverBrowserPlayBtn.disabled = mpSelectedLobby === null;
  };

  const loadMpLobbies = async (serverId: string): Promise<void> => {
    serverBrowserStatus.textContent = 'Loading rooms...';
    lobbyList.textContent = '';
    serverBrowserPlayBtn.disabled = true;
    try {
      const response = await fetch(`${edgeHttpOrigin(edgeHost)}/servers/${encodeURIComponent(serverId)}/lobbies`, { cache: 'no-store' });
      if (!response.ok) throw new Error(`HTTP ${String(response.status)}`);
      const body = (await response.json()) as { lobbies?: readonly OfficialLobbyListing[] };
      mpLobbies = body.lobbies ?? [];
      mpSelectedLobby = mpLobbies.find((l) => l.id === 'free-play') ?? mpLobbies[0] ?? null;
      serverBrowserStatus.textContent = mpSelectedServer?.label ?? edgeHost;
      renderLobbyList();
    } catch (err: unknown) {
      console.error('[menu] Failed to load lobbies:', err);
      mpLobbies = [];
      mpSelectedLobby = null;
      serverBrowserStatus.textContent = 'Unavailable';
      renderLobbyList();
    }
  };

  const openMultiplayer = async (): Promise<void> => {
    serverBrowserOverlay.classList.add('open');
    serverBrowserOverlay.setAttribute('aria-hidden', 'false');
    serverBrowserStatus.textContent = 'Loading...';
    serverList.textContent = '';
    lobbyList.textContent = '';
    serverBrowserPlayBtn.disabled = true;
    try {
      const response = await fetch(`${edgeHttpOrigin(edgeHost)}/servers`, { cache: 'no-store' });
      if (!response.ok) throw new Error(`HTTP ${String(response.status)}`);
      const body = (await response.json()) as { servers?: readonly OfficialServerListing[] };
      mpServers = body.servers ?? [];
      mpSelectedServer = mpServers.find((s) => s.id === 'north-america') ?? mpServers[0] ?? null;
      renderServerList();
      if (mpSelectedServer !== null) {
        await loadMpLobbies(mpSelectedServer.id);
      } else {
        serverBrowserStatus.textContent = 'No servers';
      }
    } catch (err: unknown) {
      console.error('[menu] Failed to load servers:', err);
      mpServers = [];
      mpLobbies = [];
      mpSelectedServer = null;
      mpSelectedLobby = null;
      serverBrowserStatus.textContent = 'Unavailable';
      renderServerList();
      renderLobbyList();
    }
  };

  const closeMultiplayer = (): void => {
    serverBrowserOverlay.classList.remove('open');
    serverBrowserOverlay.setAttribute('aria-hidden', 'true');
  };

  serverBrowserPlayBtn.addEventListener('click', () => {
    if (mpSelectedLobby === null || mpSelectedServer === null) return;
    const playerName = playerNameInput.value.trim().slice(0, 32) || defaultPlayerName();
    window.localStorage.setItem(PLAYER_NAME_STORAGE_KEY, playerName);
    const modeId = isGameModeId(mpSelectedLobby.mode) ? mpSelectedLobby.mode : 'freePlay';
    leaveMenu({
      modeId,
      templateId: mpSelectedLobby.mapId,
      edgeHost,
      roomId: mpSelectedLobby.roomId,
      locationHint: mpSelectedServer.locationHint,
      playerName,
      playerSkinId: defaultPlayerSkinId(),
      settings: gameSettings,
    });
  }, { signal: controller.signal });

  // ── Button handlers ──
  settingsBtn.addEventListener('click', openSettings, { signal: controller.signal });
  settingsCloseBtn.addEventListener('click', closeSettings, { signal: controller.signal });
  settingsModal.addEventListener('click', (event) => {
    if (event.target === settingsModal) closeSettings();
  }, { signal: controller.signal });

  multiplayerBtn.addEventListener('click', () => { void openMultiplayer(); }, { signal: controller.signal });
  serverBrowserClose.addEventListener('click', closeMultiplayer, { signal: controller.signal });
  serverBrowserOverlay.addEventListener('click', (event) => {
    if (event.target === serverBrowserOverlay) closeMultiplayer();
  }, { signal: controller.signal });

  importMapBtn.addEventListener('click', () => { importMapInput.click(); }, { signal: controller.signal });
  importMapInput.addEventListener('change', () => {
    const file = importMapInput.files?.[0];
    if (file === undefined) return;
    importMapInput.value = '';
    leaveMenu({ modeId: 'creative', mapFile: file, settings: gameSettings });
  }, { signal: controller.signal });

  // ── Settings controls ──
  settingsQualityOptions.addEventListener('click', (event) => {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-quality]');
    if (button === null) return;
    const quality = button.dataset['quality'];
    if (quality === undefined || !isGraphicsQuality(quality)) return;
    persistSettings({ ...gameSettings, quality, ...QUALITY_PRESETS[quality] });
  }, { signal: controller.signal });

  settingsFootViewOptions.addEventListener('click', (event) => {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-foot-view]');
    if (button === null) return;
    const footView = button.dataset['footView'];
    if (footView === undefined || !isCameraViewMode(footView)) return;
    persistSettings({ ...gameSettings, footView });
  }, { signal: controller.signal });

  settingsVehicleViewOptions.addEventListener('click', (event) => {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-vehicle-view]');
    if (button === null) return;
    const vehicleView = button.dataset['vehicleView'];
    if (vehicleView === undefined || !isCameraViewMode(vehicleView)) return;
    persistSettings({ ...gameSettings, vehicleView });
  }, { signal: controller.signal });

  renderDistanceInput.addEventListener('input', () => {
    persistSettings({ ...gameSettings, renderDistance: Number(renderDistanceInput.value) });
  }, { signal: controller.signal });

  resolutionScaleInput.addEventListener('input', () => {
    persistSettings({ ...gameSettings, resolutionScale: Number(resolutionScaleInput.value) / 100 });
  }, { signal: controller.signal });

  fpsCapInput.addEventListener('input', () => {
    persistSettings({ ...gameSettings, maxFps: Number(fpsCapInput.value) });
  }, { signal: controller.signal });

  showFpsInput.addEventListener('change', () => {
    persistSettings({ ...gameSettings, showFps: showFpsInput.checked });
  }, { signal: controller.signal });

  showDiagnosticsInput.addEventListener('change', () => {
    persistSettings({ ...gameSettings, showDiagnostics: showDiagnosticsInput.checked });
  }, { signal: controller.signal });

  playerNameInput.addEventListener('change', () => {
    const name = playerNameInput.value.trim().slice(0, 32) || defaultPlayerName();
    playerNameInput.value = name;
    window.localStorage.setItem(PLAYER_NAME_STORAGE_KEY, name);
  }, { signal: controller.signal });

  document.addEventListener('keydown', (e: KeyboardEvent) => {
    if (menu.classList.contains('hidden')) return;
    if (!settingsModal.hidden) {
      if (e.code === 'Escape') { e.preventDefault(); closeSettings(); }
      return;
    }
    if (serverBrowserOverlay.classList.contains('open')) {
      if (e.code === 'Escape') { e.preventDefault(); closeMultiplayer(); }
      return;
    }
    if (document.activeElement === playerNameInput) return;
  }, { signal: controller.signal });

  syncSettingsUi();
}

// ── Flow orchestrator ──

let currentGame: Game | null = null;
let titleScene: TitleScene | null = null;
let pauseMenu: PauseMenu | null = null;
/** AbortController for game-scoped event listeners, disposed on quit. */
let gameAbort: AbortController | null = null;

/** Show the lightweight start menu. Live 3D title rendering is opt-in via ?title3d=1. */
async function enterTitleScreen(): Promise<void> {
  try {
    const menu = document.getElementById('start-menu');
    if (menu !== null) menu.classList.remove('hidden');
    const crosshair = document.getElementById('crosshair');
    const hotbar = document.getElementById('hotbar');
    const hud = document.getElementById('hud');
    const vitals = document.getElementById('vitals-hud');
    const diagnostics = document.getElementById('diagnostics-hud');
    const coordinates = document.getElementById('coordinates-hud');
    const interactionPrompt = document.getElementById('interaction-prompt');
    if (crosshair !== null) crosshair.style.display = 'none';
    if (hotbar !== null) hotbar.style.display = 'none';
    if (hud !== null) hud.style.display = 'none';
    if (vitals !== null) vitals.style.display = 'none';
    if (coordinates !== null) coordinates.style.display = 'none';
    if (interactionPrompt !== null) interactionPrompt.style.display = 'none';
    diagnostics?.classList.remove('is-visible');
    diagnostics?.setAttribute('aria-hidden', 'true');

    setupStartMenu((request) => {
      if (titleScene !== null) {
        titleScene.dispose();
        titleScene = null;
      }
      void launchGame(request);
    });

    if (new URLSearchParams(window.location.search).get('title3d') === '1') {
      showLoading('Loading 3D menu...');
      const { TitleScene } = await import('@game/ui/TitleScene');
      titleScene = new TitleScene(['default']);
      await titleScene.start(canvas);
    }

    hideLoading();
  } catch (err: unknown) {
    console.error(err);
    showLoading('Error loading assets. Check console.');
  }
}

/** Create and init a Game, wire the pause menu. */
async function launchGame(request: PlayRequest): Promise<void> {
  setLoadingProgress(0.01, 'Loading world…');

  // Restore HUD in case it was hidden by a previous quit-to-menu
  const crosshair = document.getElementById('crosshair');
  const hotbar = document.getElementById('hotbar');
  const hud = document.getElementById('hud');
  const vitals = document.getElementById('vitals-hud');
  const diagnostics = document.getElementById('diagnostics-hud');
  const coordinates = document.getElementById('coordinates-hud');
  const interactionPrompt = document.getElementById('interaction-prompt');
  if (crosshair !== null) crosshair.style.display = '';
  if (hotbar !== null) hotbar.style.display = '';
  if (hud !== null) hud.style.display = '';
  if (vitals !== null) vitals.style.display = '';
  if (coordinates !== null) coordinates.style.display = '';
  if (interactionPrompt !== null) interactionPrompt.style.display = '';
  diagnostics?.classList.remove('is-visible');
  diagnostics?.setAttribute('aria-hidden', 'true');

  try {
    const { Game } = await import('@game/game/Game');
    // Inject transport from main.ts (SOLID: Game doesn't import concrete backends)
    const params = new URLSearchParams(window.location.search);
    const serverUrl = params.get('server');
    const edgeHost = request.edgeHost ?? params.get('edge');
    const edgeRoomId = request.roomId ?? params.get('room');
    const transport = (edgeHost !== null && edgeHost !== '')
      ? new PartyTransport({
          host: normalizeEdgeHost(edgeHost),
          ...(edgeRoomId !== null && edgeRoomId !== '' ? { roomId: edgeRoomId } : {}),
          mode: request.modeId,
          mapId: request.templateId ?? 'default',
          playerName: request.playerName ?? defaultPlayerName(),
          playerSkinId: request.playerSkinId ?? defaultPlayerSkinId(),
          ...(request.locationHint !== undefined ? { locationHint: request.locationHint } : {}),
        })
      : (serverUrl !== null && serverUrl !== '')
        ? new ColyseusTransport(serverUrl)
        : undefined;
    const game = new Game(canvas, ['default'], request.modeId, request.templateId, transport, request.settings);
    currentGame = game;
    await game.init();
    if (currentGame !== game) {
      game.dispose();
      return;
    }

    if (request.mapFile !== undefined) {
      showLoading('Importing map...');
      await game.loadMapFile(request.mapFile);
    }
    hideLoading();
    console.log(
      `[main] Loaded ${String(Object.keys(game.getMergedData()?.blocks ?? {}).length)} blocks`
    );

    // Wire pause menu
    pauseMenu = new PauseMenu('#pause-menu');
    pauseMenu.onResume = (): void => {
      pauseMenu?.hide();
      if (currentGame !== null) {
        currentGame.paused = false;
        void canvas.requestPointerLock().catch(() => {});
      }
    };
    pauseMenu.onQuitToMenu = (): void => {
      pauseMenu?.hide();
      quitToMenu();
    };
    pauseMenu.onSettings = (): void => {
      // Toggle the settings modal if present (start-menu settings).
      const modal = document.getElementById('settings-modal');
      if (modal !== null) {
        const open = modal.classList.toggle('open');
        modal.setAttribute('aria-hidden', open ? 'false' : 'true');
      }
    };
    pauseMenu.onRefreshTasks = (): void => {
      currentGame?.refreshFreePlayTasksPublic();
    };

    // Periodically refresh the pause menu state while it is open.
    // (interval is cleared when the game-scoped AbortController aborts)
    const pauseRefresh = window.setInterval(() => {
      if (pauseMenu === null || !pauseMenu.isOpen || currentGame === null) return;
      pauseMenu.update(currentGame.getPauseMenuState());
    }, 500);

    // Set up cleanable game-scoped listeners
    gameAbort = new AbortController();
    const { signal } = gameAbort;
    signal.addEventListener('abort', () => { window.clearInterval(pauseRefresh); });

    // Ctrl+L: load a downloaded .schematic, .schem, or .litematic file
    document.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.ctrlKey && e.code === 'KeyL') {
        e.preventDefault();
        const fileInput = document.getElementById('schematic-input') as HTMLInputElement | null;
        fileInput?.click();
      }
    }, { signal });

    // File input: load a downloaded map/structure into world
    const fileInput = document.getElementById('schematic-input') as HTMLInputElement | null;
    if (fileInput !== null) {
      fileInput.addEventListener('change', () => {
        const file = fileInput.files?.[0];
        if (file !== undefined && currentGame !== null) {
          void currentGame.loadMapFile(file);
          fileInput.value = ''; // Allow re-loading same file
        }
      }, { signal });
    }

    // ESC during gameplay toggles pause
    document.addEventListener(
      'keydown',
      (e) => {
        if (e.code !== 'Escape') return;
        if (currentGame === null || pauseMenu === null) return;
        if (currentGame.isLocalPlayerDead || currentGame.isDeathScreenOpen) return;
        if (pauseMenu.isOpen) {
          pauseMenu.hide();
          currentGame.paused = false;
          void canvas.requestPointerLock().catch(() => {});
        } else {
          currentGame.paused = true;
          pauseMenu.show();
          if (document.pointerLockElement !== null) {
            document.exitPointerLock();
          }
        }
      },
      { signal }
    );

    // PG3D-style meta HUD buttons request the pause/map overlay.
    window.addEventListener(
      'voxel:pause-toggle',
      () => {
        if (currentGame === null || pauseMenu === null) return;
        if (currentGame.isLocalPlayerDead || currentGame.isDeathScreenOpen) return;
        if (pauseMenu.isOpen) {
          pauseMenu.hide();
          currentGame.paused = false;
          void canvas.requestPointerLock().catch(() => {});
        } else {
          currentGame.paused = true;
          pauseMenu.show();
          if (document.pointerLockElement !== null) {
            document.exitPointerLock();
          }
        }
      },
      { signal }
    );
  } catch (err: unknown) {
    console.error(err);
    showLoading('Error loading assets. Check console.');
  }
}

/** Quit the current game and return to the title screen. */
function quitToMenu(): void {
  if (currentGame !== null) {
    currentGame.dispose();
    currentGame = null;
  }
  // Clean up game-scoped event listeners
  if (gameAbort !== null) {
    gameAbort.abort();
    gameAbort = null;
  }
  pauseMenu = null;

  // Hide HUD elements (restored in launchGame)
  const crosshair = document.getElementById('crosshair');
  const hotbar = document.getElementById('hotbar');
  const hud = document.getElementById('hud');
  const vitals = document.getElementById('vitals-hud');
  const coordinates = document.getElementById('coordinates-hud');
  const diagnostics = document.getElementById('diagnostics-hud');
  const interactionPrompt = document.getElementById('interaction-prompt');
  if (crosshair !== null) crosshair.style.display = 'none';
  if (hotbar !== null) hotbar.style.display = 'none';
  if (hud !== null) hud.style.display = 'none';
  if (vitals !== null) vitals.style.display = 'none';
  if (coordinates !== null) coordinates.style.display = 'none';
  if (interactionPrompt !== null) interactionPrompt.style.display = 'none';
  diagnostics?.classList.remove('is-visible');
  diagnostics?.setAttribute('aria-hidden', 'true');

  void enterTitleScreen();
}

// ── Start ──

void enterTitleScreen().catch((err: unknown) => {
  console.error('Fatal title screen error:', err);
});
